import React from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Divider, IconButton, Typography } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp, ChevronLeft, Close, ExpandLessOutlined, Lock } from '@material-ui/icons';
import CancelIcon from '@material-ui/icons/Cancel';
import LockableSectionHeader from './LockableSectionHeader.comp.jsx';
import useSidebar from '../../../../hooks/LearnTab/useSidebar';
import { DQC_COLORS } from '../../../../../../../utils/constants';
import { useColorMode } from '../../../../../../../context/colorMode/colorMode.context';
import { tUi as defaultTUi } from '../../../../../../../utils/dq_lc';

const flattenSessions = (modulesData = []) => {
    const out = [];
    modulesData.forEach((module, moduleIndex) => {
        (module.units || []).forEach((unit, unitIndex) => {
            (unit.lessons || []).forEach((session) => {
                out.push({
                    ...session,
                    _moduleIndex: moduleIndex,
                    _moduleName: module.module_name,
                    _unitIndex: unitIndex,
                    _unitTitle: unit.unit_title,
                });
            });
        });
    });
    return out;
};

const sessionActivitiesLine = (session, tUi, uiLocale) => {
    if (typeof session?.activities === 'string') return session.activities;
    if (Array.isArray(session?.activities))      return session.activities.join(' · ');
    if (Array.isArray(session?.segments))        return session.segments.map(s => s?.name || s).filter(Boolean).join(' · ');
    if (typeof session?.summary === 'string')    return session.summary;
    return (tUi || defaultTUi)('preview.sidebar.sessionActivitiesLine', uiLocale);
};

const ProgramSidebarCatalog = ({
    modulesData,
    classes,
    handleToggleModuleDropdown,
    tUi,
    uiLocale,
    dqcoList,
    handleModuleCodeClick,
    dqcoLoadingCode,
    popupContent,
    setPopupContent,
    openModules,
    openUnits,
    handleToggleUnitDropdown,
    selectedSession,
    embedded,
    firstSessionCode,
    lockedSessionCodes,
    contentStatus,
    handleToggleSessionDropdown,
    openSessions,
    slidesLoading,
    segments,
    isSegmentActive,
    assessmentVisibility,
    handleOpenAssessmentModal,
    handleViewSegment,
    isMobile,
    toggleCollapse,
    // ── embedded-only callbacks ───────────────────────────────────────────
    onViewOutline,
    onViewSession,
    onUpgrade,
    onSignUp,
    isGuest = true,
    isPublicPreview = false,
}) => {
    const { isDark } = useColorMode();

    // Hook must run on every render — keep it above any early return.
    const {
        isModuleActive,
        isUnitActive,
        getSessionUiState,
    } = useSidebar({
        embedded,
        firstSessionCode,
        lockedSessionCodes,
        contentStatus,
        isPublicPreview,
    });

    // Embedded version — module/unit accordion (mirrors the non-embedded outline) with locked-session upsell per card.
    if (embedded) {
        const flatSessions = flattenSessions(modulesData);
        // Free plans usually lock everything after the first N sessions; paid plans pass an empty set,
        // so every session is unlocked.
        const lockedArr = Array.from(lockedSessionCodes || []);
        const sessionNumberByCode = new Map(flatSessions.map((s, i) => [s.session_code, i + 1]));

        const renderSessionCard = (session) => {
            const locked = lockedArr.includes(session.session_code);
            const number = sessionNumberByCode.get(session.session_code);
            const isOpen = !!openSessions[session.session_code];
            return (
                <Box
                    key={session.session_code}
                    sx={{
                        background: locked ? 'var(--brand-bg, #FAFAFA)' : 'var(--brand-surface, #fff)',
                        borderRadius: '12px', p: 1.5, mb: 1.5, ml: 2,
                        border: '1px solid var(--brand-border, #EBEBEB)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: locked ? 'var(--brand-border, #F0F0F0)' : 'rgba(255,105,0,0.1)',
                            color: locked ? 'var(--brand-text-muted, #999)' : 'rgba(255,105,0,1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700, flexShrink: 0, mt: 0.2,
                        }}>
                            {number}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontSize: 14, fontWeight: locked ? 600 : 700, color: locked ? 'var(--brand-text-muted, #666)' : 'var(--brand-text, #1a1a1a)', lineHeight: 1.4, marginBottom: 4 }}>
                                {session.lesson_title}
                            </Typography>
                            <Typography style={{ fontSize: 12, color: locked ? 'var(--brand-text-muted, #aaa)' : 'var(--brand-text-muted, #888)' }}>
                                {sessionActivitiesLine(session, tUi, uiLocale)}
                            </Typography>
                        </Box>
                        {locked ? (
                            <Button
                                onClick={isGuest ? onSignUp : onUpgrade}
                                startIcon={<Lock style={{ fontSize: 13, color: '#fff' }} />}
                                style={{
                                    background: 'rgba(255,105,0,1)', color: '#fff',
                                    fontSize: 12, fontWeight: 600, textTransform: 'none',
                                    borderRadius: 8, padding: '5px 12px', flexShrink: 0,
                                    boxShadow: 'none',
                                }}
                            >
                                Unlock
                            </Button>
                        ) : (
                            <Button
                                onClick={() => {
                                    if (onViewSession) onViewSession(session);
                                    else if (handleToggleSessionDropdown) handleToggleSessionDropdown(session);
                                }}
                                style={{
                                    border: '1px solid var(--brand-border, #1a1a1a)', background: 'var(--brand-surface, #fff)',
                                    color: 'var(--brand-text, #1a1a1a)', fontSize: 12, fontWeight: 600,
                                    textTransform: 'none', borderRadius: 8, padding: '5px 14px',
                                    flexShrink: 0,
                                }}
                            >
                                {tUi ? tUi('preview.sidebar.view', uiLocale) : 'View'}
                            </Button>
                        )}
                    </Box>

                    {/* Segment breakdown — buildSegmentsList already excludes Module Score (Seg5_1) and pre/post assessments in embedded mode */}
                    {!locked && isOpen && (
                        <Box sx={{ mt: 1.5, pl: 4.75 }}>
                            {slidesLoading && selectedSession?.session_code === session.session_code ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                                    <CircularProgress size={16} style={{ color: '#ED6628' }} />
                                    <Typography style={{ marginLeft: 8, fontSize: 12, color: 'var(--brand-text-muted, #888)' }}>
                                        {tUi ? tUi('preview.sidebar.loadingSegments', uiLocale) : 'Loading segments…'}
                                    </Typography>
                                </Box>
                            ) : (
                                segments.map((segment, segIdx) => {
                                    const isActive = isSegmentActive(segment.id) && selectedSession?.session_code === session.session_code;
                                    return (
                                        <Box
                                            key={segment.id || segIdx}
                                            sx={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                py: 0.75, borderBottom: segIdx < segments.length - 1 ? '1px solid var(--brand-border, #F0F0F0)' : 'none',
                                            }}
                                        >
                                            <Typography style={{ fontSize: 13, color: isActive ? '#ED6628' : 'var(--brand-text, #1a1a1a)', fontWeight: isActive ? 600 : 400 }}>
                                                {segment.name}
                                            </Typography>
                                            <Button
                                                size="small"
                                                disabled={slidesLoading}
                                                onClick={() => handleViewSegment(session, segment.id)}
                                                style={{
                                                    minWidth: 'auto', padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                                    textTransform: 'none', borderRadius: 6,
                                                    backgroundColor: isActive ? '#ED6628' : '#0097B2', color: '#fff',
                                                }}
                                            >
                                                {tUi ? tUi('preview.sidebar.view', uiLocale) : 'View'}
                                            </Button>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    )}
                </Box>
            );
        };

        return (
            <Box sx={{ background: 'var(--brand-surface, #fff)', display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Top: View Program Outline button */}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', flexShrink: 0, borderBottom: '1px solid var(--brand-border, #F0F0F0)', gap: 1 }}>
                    {toggleCollapse ? (
                        <IconButton
                            onClick={toggleCollapse}
                            disableRipple disableFocusRipple
                            title="Collapse session list"
                            style={{
                                flexShrink: 0, cursor: 'pointer', outline: 'none',
                                color: '#FF6900', backgroundColor: 'var(--brand-surface, #fff)',
                                border: '1px solid var(--brand-border, #e6e9ef)',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            }}
                        >
                            {isMobile ? <ExpandLessOutlined style={{ fontSize: 22 }} /> : <ChevronLeft style={{ fontSize: 22 }} />}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 40, flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            onClick={onViewOutline}
                            style={{
                                background: 'rgba(255,105,0,1)',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 15,
                                textTransform: 'none',
                                borderRadius: 999,
                                padding: '10px 20px',
                                boxShadow: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            View program outline
                        </Button>
                    </Box>
                    <Box sx={{ width: 40, flexShrink: 0 }} />
                </Box>

                {/* Module → Unit → Session accordion (mirrors the non-embedded outline) */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, background: 'var(--brand-surface, #fff)' }}>
                    {modulesData.map((module, moduleIndex) => {
                        const isModuleOpen = !!openModules[moduleIndex];
                        return (
                            <Box key={module.module_code || moduleIndex} sx={{ mb: 1 }}>
                                <Box
                                    onClick={() => handleToggleModuleDropdown(moduleIndex)}
                                    sx={{ cursor: 'pointer', px: 1, py: 1 }}
                                >
                                    <LockableSectionHeader
                                        label={tUi ? tUi('preview.sidebar.moduleWithIndex', uiLocale, { index: moduleIndex + 1, title: module.module_name || '' }) : `Module ${moduleIndex + 1}: ${module.module_name || ''}`}
                                        isActive
                                        isOpen={isModuleOpen}
                                        titleVariant="subtitle1"
                                        titleStyle={{ fontSize: 14, fontWeight: 800 }}
                                        activeTitleColor="var(--brand-text, #1a1a1a)"
                                        activeArrowColor="#FF6900"
                                        showLockWhenInactive={false}
                                    />
                                </Box>

                                {isModuleOpen && (module.units || []).map((unit, unitIndex) => {
                                    const unitKey = `${moduleIndex}-${unitIndex}`;
                                    const isUnitOpen = !!openUnits[unitKey];
                                    return (
                                        <Box key={unit.unit_code || unitIndex} sx={{ pl: 2, mb: 0.5 }}>
                                            <Box
                                                onClick={() => handleToggleUnitDropdown(moduleIndex, unitIndex)}
                                                sx={{ cursor: 'pointer', px: 1, py: 0.75 }}
                                            >
                                                <LockableSectionHeader
                                                    label={tUi ? tUi('preview.sidebar.unitWithIndex', uiLocale, { index: unitIndex + 1, title: unit.unit_title || '' }) : `Unit ${unitIndex + 1}: ${unit.unit_title || ''}`}
                                                    isActive
                                                    isOpen={isUnitOpen}
                                                    titleVariant="body1"
                                                    titleStyle={{ fontSize: 13, fontWeight: 700 }}
                                                    activeTitleColor="var(--brand-text-muted, #666)"
                                                    activeArrowColor="#FF6900"
                                                    showLockWhenInactive={false}
                                                />
                                            </Box>

                                            {isUnitOpen && (unit.lessons || []).map(renderSessionCard)}
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>

                {/* Dark upsell strip + Sign Up CTA — only shown to guest users */}
                {isGuest && (
                    <Box sx={{ background: '#1a1a1a', p: 2.5, flexShrink: 0 }}>
                        <Typography style={{ fontSize: 14, color: '#fff', lineHeight: 1.6, marginBottom: 14, fontWeight: 800 }}>
                           You’ve seen what one session can do. Unlock the full program, multi-language delivery, and impact reporting.
                        </Typography>
                        <Button
                            onClick={onSignUp}
                            style={{
                                background: 'linear-gradient(to right, rgba(183,211,52,1) -20%, rgba(255,105,0,1) 40%)',
                                color: '#fff', fontWeight: 400,
                                fontSize: 15, textTransform: 'none', borderRadius: '4px',
                                padding: '12px 20px', width: '83%', boxShadow: 'none',
                            }}
                        >
                            Sign up to save your program
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: '20px' }}>
        {modulesData.map((module, moduleIndex) => {
            const moduleActive = isModuleActive(moduleIndex);
            return (
                <Card className={classes.card} key={module.module_code}>
                    <CardContent className={classes.cardContent}>
                        <Box 
                            onClick={() => {
                                if (moduleActive) handleToggleModuleDropdown(moduleIndex);
                            }} 
                            style={{ cursor: moduleActive ? 'pointer' : 'not-allowed' }}
                        >
                            <LockableSectionHeader
                                label={tUi('preview.sidebar.moduleWithIndex', uiLocale, { index: moduleIndex + 1, title: module.module_name })}
                                isActive={moduleActive}
                                isOpen={!!openModules[moduleIndex]}
                                titleVariant="h6"
                                titleStyle={{ fontSize: '1.2rem' }}
                                activeTitleColor="#ED6628"
                                inactiveTitleColor="#b6b6b6"
                                activeArrowColor="#D35400"
                                inactiveArrowColor="#c8c8c8"
                            />

                            <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                {module.unique_dqco?.map((dqco, idx) => {
                                    const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);
                                    return (
                                        <span
                                            key={dqco || idx}
                                            style={{
                                                backgroundColor: moduleActive ? (DQC_COLORS[dqcoObj?.dqc_no] || '#D2DD2C') : '#d9d9d9',
                                                color: moduleActive ? '#ffffff' : '#8f8f8f',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                padding: '1px 7px',
                                                fontWeight: 500,
                                                cursor: moduleActive ? 'pointer' : 'not-allowed',
                                                margin: '4px',
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (!moduleActive || dqcoLoadingCode) return;
                                                handleModuleCodeClick(module, dqco);
                                            }}
                                        >
                                            {dqco}
                                            {dqcoLoadingCode === dqco && (
                                                <CircularProgress size={10} thickness={6} style={{ color: '#ffffff', marginLeft: 6, verticalAlign: 'middle' }} />
                                            )}
                                        </span>
                                    );
                                })}
                            </Box>
                        </Box>
                        {popupContent && (
                            <>
                                <Box
                                    onClick={() => setPopupContent(null)}
                                    style={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        width: '100vw',
                                        height: '100vh',
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                        zIndex: 1299,
                                    }}
                                />
                                <Box
                                    style={{
                                        position: 'fixed',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '80%',
                                        maxWidth: '500px',
                                        backgroundColor: 'var(--brand-surface, #fff)',
                                        border: '1px solid var(--brand-border, #000)',
                                        borderRadius: '20px',
                                        boxShadow: 24,
                                        padding: 4,
                                        zIndex: 1300,
                                    }}
                                >
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setPopupContent(null)}
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 0,
                                            color: 'var(--brand-text, #000)',
                                        }}
                                    >
                                        <Close />
                                    </IconButton>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '15px 20px' }}>
                                        <Box sx={{ backgroundColor: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C', color: 'white', borderRadius: '20px', px: 2, py: 1, fontWeight: 'medium', mb: 2 }}>
                                            {popupContent.code}
                                        </Box>
                                        <Typography variant="h5" component="h2" style={{ fontWeight: 'bold', textAlign: 'center', color: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C' }}>
                                            {popupContent.title}
                                        </Typography>
                                        <Typography style={{ mt: 2, whiteSpace: 'pre-line', textAlign: 'left', fontSize: '11px', color: 'var(--brand-text, #000)' }}>
                                            {popupContent.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                        {openModules[moduleIndex] && (
                            <>
                                {module.units.map((unit, unitIndex) => {
                                    const unitActive = isUnitActive(moduleIndex, unitIndex);
                                    return (
                                        <Card style={{ boxShadow: 'none', backgroundColor: 'transparent' }} key={unit.unit_code}>
                                            <CardContent>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    onClick={() => {
                                                        if (unitActive) handleToggleUnitDropdown(moduleIndex, unitIndex);
                                                    }}
                                                    style={{ cursor: unitActive ? 'pointer' : 'not-allowed' }}
                                                >
                                                    <LockableSectionHeader
                                                        label={tUi('preview.sidebar.unitWithIndex', uiLocale, { index: unitIndex + 1, title: unit.unit_title })}
                                                        isActive={unitActive}
                                                        isOpen={!!openUnits[`${moduleIndex}-${unitIndex}`]}
                                                        titleVariant="h6"
                                                        activeTitleColor="#e7456d"
                                                        inactiveTitleColor="#b6b6b6"
                                                        activeArrowColor="#ED6628"
                                                        inactiveArrowColor="#c8c8c8"
                                                    />
                                                </Box>

                                                {openUnits[`${moduleIndex}-${unitIndex}`] && (
                                                    <>
                                                        <Divider style={{ height: '4px', marginTop: '10px', backgroundColor: unitActive ? '#F17632' : '#dedede', borderRadius: '15px' }} />
                                                        {unit.lessons.map((session, sessionIndex) => (
                                                            <Box key={session.session_code}>
                                                                {(() => {
                                                                    const { locked, status, cursor, opacity } = getSessionUiState(moduleIndex, unitIndex, session.session_code);
                                                                    return (
                                                                <Box
                                                                    className={classes.lessonContainer}
                                                                    style={{
                                                                        backgroundColor: selectedSession?.session_code === session.session_code ? 'rgba(238, 102, 40, 0.15)' : 'transparent',
                                                                        borderRadius: selectedSession?.session_code === session.session_code ? '8px' : '0px',
                                                                        cursor,
                                                                        opacity
                                                                    }}
                                                                    onClick={() => {
                                                                        if (isPublicPreview || (!locked && status === 'done')) {
                                                                            handleToggleSessionDropdown(session);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Box className={classes.sectionRow}>
                                                                        <LockableSectionHeader
                                                                            label={tUi('preview.sidebar.sessionWithIndex', uiLocale, { index: sessionIndex + 1, title: session.lesson_title })}
                                                                            isActive={!locked}
                                                                            isOpen={!!openSessions[session.session_code]}
                                                                            titleVariant="h4"
                                                                            titleClassName={classes.sectionText}
                                                                            activeTitleColor="#5A5A5A"
                                                                            inactiveTitleColor="#aaa"
                                                                            rightNode={(() => {
                                                                                if (locked) return <Lock style={{ color: '#ccc', fontSize: 18 }} />;
                                                                                if (status === 'in_progress') return <CircularProgress size={20} style={{ color: '#ED6628' }} />;
                                                                                if (status === 'failed') return <CancelIcon style={{ color: '#FF5757' }} />;
                                                                                return openSessions[session.session_code] ? <ArrowDropUp style={{ color: '#ED6628' }} /> : <ArrowDropDown style={{ color: '#ED6628' }} />;
                                                                            })()}
                                                                            showChevron={false}
                                                                            showLockWhenInactive={false}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                                    );
                                                                })()}

                                                                {openSessions[session.session_code] && (
                                                                    <Box style={{ marginTop: '8px' }}>
                                                                        {slidesLoading && selectedSession?.session_code === session.session_code ? (
                                                                            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                                                                                <CircularProgress size={20} style={{ color: '#ED6628' }} />
                                                                                <Typography variant="body2" style={{ marginLeft: '10px', color: '#555' }}>
                                                                                    {tUi('preview.sidebar.loadingSegments', uiLocale)}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            segments.map((segment, segIdx) => {
                                                                                const isActive = isSegmentActive(segment.id) && selectedSession?.session_code === session.session_code;
                                                                                const isAssessmentSegment = segment.id === '__PRE_ASSESSMENT__' || segment.id === '__POST_ASSESSMENT__';
                                                                                const assessmentType = segment.id === '__PRE_ASSESSMENT__' ? 'pre' : 'post';
                                                                                const sessionVisibility = assessmentVisibility[session.session_code] || {};
                                                                                const isVisible = segment.id === '__PRE_ASSESSMENT__'
                                                                                    ? (sessionVisibility.show_pre_assessment !== false)
                                                                                    : (sessionVisibility.show_post_assessment !== false);

                                                                                return (
                                                                                    <Box
                                                                                        className={classes.lessonContainer}
                                                                                        key={segment.id || segIdx}
                                                                                        style={{
                                                                                            backgroundColor: isActive ? 'rgba(238, 102, 40, 0.1)' : 'transparent',
                                                                                            borderRadius: isActive ? '8px' : '0',
                                                                                            padding: '8px 12px 8px 20px',
                                                                                            marginBottom: '4px',
                                                                                            marginTop: isAssessmentSegment ? '15px' : '0px',
                                                                                            boxSizing: 'border-box',
                                                                                        }}
                                                                                    >
                                                                                        <Box className={classes.sectionSegmentRow} style={{ minWidth: 0 }}>
                                                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', flex: 1, minWidth: 0 }}>
                                                                                                <Typography
                                                                                                    variant="h6"
                                                                                                    className={classes.sectionText}
                                                                                                    sx={{
                                                                                                        color: isActive ? '#ED6628' : '#000000',
                                                                                                        fontWeight: isActive ? 600 : 400,
                                                                                                        flex: '1 1 auto',
                                                                                                        minWidth: 0,
                                                                                                    }}
                                                                                                >
                                                                                                    {segment.name}
                                                                                                </Typography>
                                                                                                {isAssessmentSegment && !isPublicPreview && (
                                                                                                    <Button
                                                                                                        variant="text"
                                                                                                        size="small"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            handleOpenAssessmentModal(assessmentType, isVisible);
                                                                                                        }}
                                                                                                        sx={{ minWidth: 'auto', flexShrink: 0, padding: '2px 8px', fontSize: '11px', textTransform: 'none', color: '#fff', fontWeight: 500, background: '#ff6468' }}
                                                                                                    >
                                                                                                        {isVisible ? tUi('preview.btn.hide', uiLocale) : tUi('preview.btn.unhide', uiLocale)}
                                                                                                    </Button>
                                                                                                )}
                                                                                            </Box>
                                                                                            <Button
                                                                                                variant="contained"
                                                                                                className={classes.viewButton}
                                                                                                disabled={slidesLoading}
                                                                                                onClick={() => {
                                                                                                    handleViewSegment(session, segment.id);
                                                                                                    if (isMobile) {
                                                                                                        toggleCollapse();
                                                                                                    }
                                                                                                }}
                                                                                                style={{ backgroundColor: isActive ? '#ED6628' : '#0097B2', color: '#fff' }}
                                                                                            >
                                                                                                {tUi('preview.sidebar.view', uiLocale)}
                                                                                            </Button>
                                                                                        </Box>
                                                                                    </Box>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </>
                        )}
                    </CardContent>
                </Card>
            );
        })}
        </Box>
    );
};

export { ProgramSidebarCatalog };
export const SidebarCatalog = ProgramSidebarCatalog;
export default SidebarCatalog;
